import { type FC, useMemo } from "react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useWillExtraction } from "@/shared/hooks/useWillExtraction";
import { formatContactName, isWillWitness } from "@/shared/utils/contactUtils";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type { WillWitness } from "@/shared/types/probateTypes";

import familyTriageIntro from "@/shared/assets/family-triage/family-triage-intro.svg";

interface PreviewRow {
  label: string;
  detail: string;
  status: string;
  statusColor: "default" | "brand";
}

export const FamilyTriageIntroScreen: FC<FlowCustomComponentProps> = () => {
  const { person } = useAppContext();
  const { contacts } = useUserContacts();
  const estateId = person?.estate_id;
  const { data: willExtraction } = useWillExtraction(estateId);
  const name = person?.first_name ?? "the decedent";

  const previewRows = useMemo((): PreviewRow[] => {
    const willNamed = contacts.filter(
      (c) => c.extracted_from_document && !isWillWitness(c),
    );
    const contactWitnesses = contacts.filter((c) => isWillWitness(c));

    const extractionWitnesses = (willExtraction?.specific?.witness_info ?? []) as WillWitness[];
    const contactWitnessNames = new Set(
      contactWitnesses.map((c) =>
        formatContactName(c.first_name, c.last_name).toLowerCase(),
      ),
    );
    const additionalWitnesses = extractionWitnesses.filter(
      (w) => w.name && !contactWitnessNames.has(w.name.toLowerCase().trim()),
    );

    const totalWitnessCount = contactWitnesses.length + additionalWitnesses.length;
    const witnessNames = [
      ...contactWitnesses.map((c) => formatContactName(c.first_name, c.last_name)),
      ...additionalWitnesses.map((w) => w.name),
    ].filter(Boolean);

    const rows: PreviewRow[] = [
      {
        label: `${name}'s closest family`,
        detail: "We'll ask a few questions to confirm this",
        status: "Not started",
        statusColor: "default",
      },
    ];

    if (willNamed.length > 0) {
      const names = willNamed
        .map((c) => formatContactName(c.first_name, c.last_name))
        .filter(Boolean);
      const preview =
        names.length <= 3
          ? names.join(", ")
          : `${names.slice(0, 3).join(", ")}, and more`;
      rows.push({
        label: "Named in the will",
        detail: preview,
        status: `${willNamed.length} found`,
        statusColor: "brand",
      });
    }

    if (totalWitnessCount > 0) {
      rows.push({
        label: "Witnesses",
        detail: witnessNames.join(", "),
        status: `${totalWitnessCount} found`,
        statusColor: "brand",
      });
    }

    return rows;
  }, [contacts, name, willExtraction]);

  return (
    <div className="flex flex-col gap-6">
      <img
        src={familyTriageIntro}
        alt="People discussing family details"
        className="h-[260px] w-[260px]"
      />

      <div className="flex flex-col gap-1">
        <p className="font-sans text-xl font-semibold leading-[30px]">
          Confirm who the court needs to hear from
        </p>
        <p className="text-sm text-secondary tracking-[-0.28px]">
          Courts ask for a list of {name}'s closest family, anyone named in the
          will, and any listed organizations or witnesses before granting
          authority. We'll help you build and confirm this list step by step.
        </p>
      </div>

      <div className="flex flex-col divide-y divide-primary">
        {previewRows.map((row) => (
          <div key={row.label} className="flex items-center justify-between py-4">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium">{row.label}</p>
              <p className="text-[13px] text-secondary">{row.detail}</p>
            </div>
            <p
              className={
                row.statusColor === "brand"
                  ? "shrink-0 text-sm font-medium text-brand"
                  : "shrink-0 text-sm text-secondary"
              }
            >
              {row.status}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
